import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";

const Landingheader = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Real database dynamic user context state
  const [user, setUser] = useState({
    name: "",
    email: "",
    customer_image: null
  });

  // Fetch dynamic real user data safely on component load
  useEffect(() => {
    const fetchHeaderAccountData = async () => {
      try {
        const user_token = Cookies.get('user_token');
        if (!user_token) return;

        const response = await axios.post('https://land-survey-main-server.onrender.com/getAccount', { user_token });

        if (response?.data && response.data.status === true) {
          const account = response.data.account;
          setUser({
            name: account.customer_name || 'User Name',
            email: account.customer_email || 'No email attached',
            customer_image: account.customer_image || null
          });
        }
      } catch (err) {
        console.error("Failed to sync navbar data dynamically:", err.message);
      }
    };

    fetchHeaderAccountData();
  }, []);

  const handleLinkClick = () => setMenuOpen(false);

  const handleProfileNavigation = () => {
    setMenuOpen(false);
    navigate("/profile"); 
  };

  // Helper calculation to isolate the first letter of user's name gracefully
  const getInitials = (fullName) => {
    if (!fullName) return "?";
    return fullName.trim().charAt(0).toUpperCase();
  };

  // Reusable component block to handle image layout vs letter avatar fallback layout 
  const renderAvatar = (imageSizeClass, textFormatClass) => {
    if (user.customer_image) {
      return (
        <img 
          src={`https://land-survey-main-server.onrender.com/clients_images/${user.customer_image}`} 
          className={`object-cover border-2 rounded-full border-indigo-400/50 ${imageSizeClass}`}
          alt="Avatar" 
        />
      );
    }

    // Dynamic Letter Fallback if customer_image is null
    return (
      <div className={`flex items-center justify-center font-bold text-white bg-gradient-to-tr from-indigo-500 to-purple-600 rounded-full border border-indigo-300/40 shadow-sm ${imageSizeClass} ${textFormatClass}`}>
        {getInitials(user.name)}
      </div>
    );
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-blue-900/50 bg-[#1A3D6D]/95 backdrop-blur-md shadow-sm">
      <div className="flex flex-col justify-between px-4 py-4 mx-auto max-w-7xl lg:flex-row lg:items-center sm:px-6 lg:px-8">
        
        {/* Logo and Toggle Switch Row */}
        <div className="flex items-center justify-between w-full lg:w-auto">
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/images/logo.jpg" 
              className="object-cover w-10 h-10 transition-transform border-2 rounded-full border-blue-400/30 group-hover:scale-105" 
              alt="Logo" 
            />
            <h2 className="text-xl font-bold tracking-tight text-white transition-colors group-hover:text-blue-200 whitespace-nowrap">
              New World Surveyor
            </h2>
          </Link>
          
          <button 
            onClick={() => setMenuOpen(!menuOpen)}
            className="block text-slate-200 lg:hidden hover:text-white p-1.5 rounded-lg hover:bg-blue-800/50 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {menuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Navigation Dropdown Drawer Menu */}
        <nav className={`${menuOpen ? "flex" : "hidden"} lg:flex flex-col lg:flex-row w-full lg:w-auto items-center mt-4 lg:mt-0`}>
          
          {/* MOBILE ONLY PROFILE CARD */}
          <div 
            onClick={handleProfileNavigation}
            className="flex items-center w-full gap-3 p-3 mb-4 transition-all border cursor-pointer rounded-xl border-blue-800/60 bg-blue-950/40 lg:hidden hover:bg-blue-900/40"
          >
            {renderAvatar("w-12 h-12", "text-lg")}
            <div className="overflow-hidden text-left">
              <p className="text-sm font-semibold text-white truncate">{user.name}</p>
              <p className="text-xs truncate text-slate-300">{user.email}</p>
              <span className="text-[10px] text-indigo-300 font-medium underline mt-0.5 block">Update Profile →</span>
            </div>
          </div>

          <ul className="flex flex-col w-full gap-2 font-medium text-center lg:gap-8 lg:flex-row lg:items-center text-slate-200">
            <li>
              <Link to='/' onClick={handleLinkClick} className="block py-2 transition-colors hover:text-white">Home</Link>
            </li>
            <li>
              <Link to='/Landing' onClick={handleLinkClick} className="block py-2 transition-colors hover:text-white">Services</Link>
            </li>
            <li>
              <Link to='/progress' onClick={handleLinkClick} className="block py-2 transition-colors hover:text-white">Request Progress</Link>
            </li>
            <li>
              <Link to='/contact' onClick={handleLinkClick} className="block py-2 transition-colors hover:text-white">Contact Us</Link>
            </li>
            
            <div className="w-full h-px my-2 bg-blue-800/60 lg:hidden" />

            {/* DESKTOP ONLY PROFILE ACTION CHIP */}
            <li className="hidden lg:block">
              <button 
                onClick={handleProfileNavigation}
                className="flex items-center gap-3 p-1.5 pr-4 text-left rounded-full bg-blue-950/40 hover:bg-blue-900/60 border border-blue-800/40 transition-all group"
                title="Manage Account Profile"
              >
                {renderAvatar("w-8 h-8", "text-xs")}
                <div className="hidden xl:block max-w-[120px]">
                  <p className="text-xs font-semibold text-white truncate">{user.name}</p>
                  <p className="text-[10px] text-slate-300 truncate">{user.email}</p>
                </div>
              </button>
            </li>

            <li>
              <button 
                onClick={() => window.confirm('Logout?') && navigate('/logout')} 
                className="inline-block w-full lg:w-auto px-5 py-2.5 text-sm font-semibold text-center text-white bg-blue-600 hover:bg-blue-500 rounded-full transition-all shadow-md"
              >
                Logout
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Landingheader;